/**
 * 颜色工具函数
 * 用于动态生成主题色相关的颜色变体
 */

/**
 * HSL 颜色对象
 */
interface HSL {
  h: number; // 色相 0-360
  s: number; // 饱和度 0-100
  l: number; // 亮度 0-100
}

/**
 * 将十六进制颜色转换为 HSL
 * @param hex 十六进制颜色值，如 #7B61FF 或 7B61FF
 * @returns HSL 颜色对象
 */
export function hexToHsl(hex: string): HSL {
  // 移除 # 号
  const cleanHex = hex.replace('#', '');
  
  // 解析 RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * 将 HSL 颜色转换为十六进制
 * @param hsl HSL 颜色对象
 * @returns 十六进制颜色值，如 #7B61FF
 */
export function hslToHex(hsl: HSL): string {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // 无彩色
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * 将十六进制颜色转换为 RGB 值（逗号分隔格式）
 * @param hex 十六进制颜色值，如 #7B61FF
 * @returns RGB 值，如 "123, 97, 255"
 */
export function hexToRgb(hex: string): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * 生成渐变色的浅色和深色变体
 * @param primaryColor 主色值，如 #7B61FF
 * @returns 包含浅色和深色的对象
 */
export function generateGradientColors(primaryColor: string): {
  light: string;
  dark: string;
} {
  const hsl = hexToHsl(primaryColor);

  // 浅色：增加亮度，降低饱和度
  const light: HSL = {
    h: hsl.h,
    s: Math.max(0, Math.min(100, hsl.s - 10)), // 降低10%饱和度，限制在0-100
    l: Math.max(0, Math.min(100, hsl.l + 12)) // 增加12%亮度，限制在0-100
  };

  // 深色：降低亮度，保持饱和度
  const dark: HSL = {
    h: hsl.h,
    s: hsl.s, // 保持饱和度
    l: Math.max(0, Math.min(100, hsl.l - 14)) // 降低14%亮度，限制在0-100
  };

  return {
    light: hslToHex(light),
    dark: hslToHex(dark)
  };
}

/**
 * 应用主题色到 CSS 变量
 * @param primaryColor 主色值，如 #7B61FF
 */
export function applyThemeColor(primaryColor: string): void {
  const root = document.documentElement;
  
  // 设置主色
  root.style.setProperty('--primary-color', primaryColor);
  
  // 设置 RGB 值（用于 rgba()）
  const rgb = hexToRgb(primaryColor);
  root.style.setProperty('--primary-color-rgb', rgb);
  
  // 生成并设置渐变色
  const { light, dark } = generateGradientColors(primaryColor);
  root.style.setProperty('--primary-color-light', light);
  root.style.setProperty('--primary-color-dark', dark);
  
  // 设置渐变背景（用于直接使用）
  root.style.setProperty(
    '--primary-gradient',
    `linear-gradient(135deg, ${light} 0%, ${dark} 100%)`
  );
}

/**
 * 重置主题色为默认值
 */
export function resetThemeColor(): void {
  const defaultColor = '#7B61FF';
  applyThemeColor(defaultColor);
}

